import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { ToastContainer } from "react-toastify";

import { appTheme } from "./theme";
import { queryClient } from "./lib/queryClient";
import ProtectedRoute from "./ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";

// Route-level code-splitting: each page (and its heavy deps — AG Grid, MUI,
// date pickers) loads on demand instead of bloating the initial bundle.
const Login = lazy(() => import("./Login"));
const RoomPage = lazy(() => import("./pages/rooms").then((m) => ({ default: m.RoomPage })));
const ReferentPage = lazy(() =>
  import("./pages/referents").then((m) => ({ default: m.ReferentPage }))
);
const StandaloneClientPage = lazy(() =>
  import("./pages/clients").then((m) => ({ default: m.StandaloneClientPage }))
);
const SearchPage = lazy(() => import("./pages/search").then((m) => ({ default: m.SearchPage })));
const SeminarEditPage = lazy(() =>
  import("./pages/seminars").then((m) => ({ default: m.SeminarEditPage }))
);
const InvoicePage = lazy(() =>
  import("./pages/invoices").then((m) => ({ default: m.InvoicePage }))
);
const SettingsPage = lazy(() =>
  import("./pages/settings").then((m) => ({ default: m.SettingsPage }))
);
const EmailExportPage = lazy(() =>
  import("./pages/emails").then((m) => ({ default: m.EmailExportPage }))
);
const EmailPreviewPage = lazy(() =>
  import("./pages/emails").then((m) => ({ default: m.EmailPreviewPage }))
);

const RouteFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <CircularProgress />
  </div>
);

// Root layout: the single Suspense boundary for the lazy route elements. Data
// mode (createBrowserRouter) — not <BrowserRouter> — because that's what enables
// useBlocker for the unsaved-edit guard in ProtectedRoute.
const RootLayout = () => (
  <Suspense fallback={<RouteFallback />}>
    <Outlet />
  </Suspense>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/login", element: <Login /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/seminare/semverwaltung",
            element: <SeminarEditPage filter={"currentmonth"} />,
          },
          {
            path: "/seminare/bestsemverwaltung",
            element: <SeminarEditPage filter={"confirmed"} />,
          },
          {
            path: "/seminare/seminarzusammenfassung",
            element: <SeminarEditPage filter={"all"} />,
          },
          { path: "/seminare/raume/verwaltung", element: <RoomPage /> },
          { path: "/seminare/referenten/verwaltung", element: <ReferentPage /> },
          { path: "/suche", element: <SearchPage /> },
          { path: "/kunden/verwaltung", element: <StandaloneClientPage /> },
          { path: "/rechnungen", element: <InvoicePage /> },
          { path: "/emailexport", element: <EmailExportPage /> },
          { path: "/emailpreview", element: <EmailPreviewPage /> },
          { path: "/options", element: <SettingsPage /> },
        ],
      },
      { path: "*", element: <Navigate to="/seminare/semverwaltung" replace /> },
    ],
  },
]);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={appTheme}>
          <AuthProvider>
            <RouterProvider router={router} />
            {/* One app-wide toast host so notify() renders on EVERY route —
                including /login (which is outside ProtectedRoute). Previously it
                lived inside ProtectedRoute gated on isAuth, so login-error toasts
                fired into the void. */}
            <ToastContainer />
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
